import React, { forwardRef } from 'react';

const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-ui)',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, transform 0.05s ease',
    border: '1px solid transparent',
    outline: 'none',
    textDecoration: 'none',
    userSelect: 'none',
    boxSizing: 'border-box'
};

const variants = {
    primary: {
        backgroundColor: 'var(--primary)',
        color: 'var(--on-primary)',
        borderColor: 'var(--primary)'
    },
    default: {
        backgroundColor: 'var(--primary)',
        color: 'var(--on-primary)',
        borderColor: 'var(--primary)'
    },
    secondary: {
        backgroundColor: 'var(--surface-sunken)',
        color: 'var(--text)',
        borderColor: 'var(--border)'
    },
    ghost: {
        backgroundColor: 'transparent',
        color: 'var(--text)',
        borderColor: 'transparent'
    },
    accent: {
        backgroundColor: 'var(--accent)',
        color: 'var(--text)',
        borderColor: 'var(--accent)'
    },
    danger: {
        backgroundColor: 'var(--danger)',
        color: 'var(--text-on-accent)',
        borderColor: 'var(--danger)'
    },
    destructive: {
        backgroundColor: 'var(--danger)',
        color: 'var(--text-on-accent)',
        borderColor: 'var(--danger)'
    },
    outline: {
        backgroundColor: 'transparent',
        color: 'var(--text)',
        borderColor: 'var(--border-strong)'
    },
    link: {
        backgroundColor: 'transparent',
        color: 'var(--accent-ink)',
        borderColor: 'transparent',
        textDecoration: 'underline'
    }
};

const sizes = {
    sm: { height: '2rem', padding: '0 0.75rem', fontSize: '0.75rem', gap: '0.375rem' },
    md: { height: '2.5rem', padding: '0 1rem', fontSize: '0.875rem', gap: '0.5rem' },
    default: { height: '2.5rem', padding: '0 1rem', fontSize: '0.875rem', gap: '0.5rem' },
    lg: { height: '2.75rem', padding: '0 1.5rem', fontSize: '1rem', gap: '0.5rem' },
    icon: { height: '2.5rem', width: '2.5rem', padding: 0, gap: 0 }
};

const Spinner = () => (
    <svg
        data-testid="button-spinner"
        style={{
            animation: 'spin 1s linear infinite',
            width: '1rem',
            height: '1rem',
            marginRight: '0.5rem',
            display: 'inline-block'
        }}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="31.4 31.4"
            strokeLinecap="round"
            style={{ opacity: 0.3 }}
        />
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="15.7 47.1"
            strokeLinecap="round"
        />
    </svg>
);

const Button = forwardRef(({
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    icon,
    startIcon,
    endIcon,
    children,
    style,
    type = 'button',
    ...props
}, ref) => {
    const variantStyle = variants[variant] || variants.primary;
    const sizeStyle = sizes[size] || sizes.md;
    const isDisabled = disabled || loading;

    const leadingIcon = icon || startIcon;

    return (
        <button
            ref={ref}
            type={type}
            disabled={isDisabled}
            aria-busy={loading ? 'true' : undefined}
            data-variant={variant}
            data-size={size}
            className={className}
            style={{
                ...baseStyle,
                ...variantStyle,
                ...sizeStyle,
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                ...style
            }}
            {...props}
        >
            {loading && <Spinner />}
            {!loading && leadingIcon && (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {leadingIcon}
                </span>
            )}
            {children}
            {!loading && endIcon && (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {endIcon}
                </span>
            )}
        </button>
    );
});

Button.displayName = 'Button';

export { Button, baseStyle as buttonStyle, variants as buttonVariants };
export default Button;
